import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme, Button, IconButton, Snackbar, Alert, TextField, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import Headeer from '../../components/Headeer';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FunctionsIcon from '@mui/icons-material/Functions';
import FormulaService from '../../services/doctorServices/FormulaService';
import CollapsibleListFilters, {
  ListFiltersExcelButton,
  listFiltersSelectProps,
} from '../../components/CollapsibleListFilters';
import { useNavigate } from 'react-router-dom';

const Formulas = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t, i18n } = useTranslation('formulas');
  const navigate = useNavigate();
  const formulaService = useMemo(() => new FormulaService(), []);

  const dateLocale = useMemo(() => {
    const lang = i18n.language || "en";
    if (lang.startsWith("tr")) return "tr-TR";
    if (lang.startsWith("fr")) return "fr-FR";
    return "en-US";
  }, [i18n.language]);

  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ name: "", description: "", dateFrom: "", dateTo: "" });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formulaToDelete, setFormulaToDelete] = useState(null);

  const fetchFormulas = async () => {
    try {
      setLoading(true);
      const response = await formulaService.getAllFormulas({
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": paginationModel.page,
          "x-bone-limit": paginationModel.pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setFormulas(Array.isArray(response?.data) ? response.data : []);
      setTotalCount(Number(response?.headers?.["x-bone-totalcount"]) || 0);
    } catch (error) {
      console.error('Error loading formulas:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchFormulas();
  }, [paginationModel.page, paginationModel.pageSize, sortBy, sortOrder, appliedFilters]);

  // Delete formula
  const handleDeleteClick = (id) => {
    setFormulaToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!formulaToDelete) return;
    
    try {
      await formulaService.deleteFormula(formulaToDelete);
      setSnackbar({
        open: true,
        message: t('formulaDeletedSuccessfully'),
        severity: "success"
      });
      setDeleteDialogOpen(false);
      setFormulaToDelete(null);
      fetchFormulas(); // Refresh list
    } catch (error) {
      console.error('Error deleting formula:', error);
      setDeleteDialogOpen(false);
      // 403 (Permission) errors are handled by global interceptor
      if (error.response?.status !== 403) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || t('errorDeletingFormula');
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
      // For 403 errors, global alert will be shown automatically
    }
  };

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('formulaName'),
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FunctionsIcon sx={{ color: colors.blueAccent[400], fontSize: '20px' }} />
          <Box>
            <Box sx={{ fontWeight: 600, color: colors.grey[100] }}>
              {params.row.name}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Box 
          sx={{ 
            color: colors.grey[300],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={params.row.description}
        >
          {params.row.description || t('noDescription')}
        </Box>
      ),
    },
    {
      field: 'formula_expression',
      headerName: t('formulaExpression'),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            backgroundColor: `${colors.primary[400]}80`,
            padding: '4px 8px',
            borderRadius: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: colors.greenAccent[400],
            border: `1px solid ${colors.greenAccent[700]}40`,
          }}
          title={params.row.formula_expression}
        >
          {params.row.formula_expression}
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: t('created'),
      width: 130,
      sortable: true,
      /* Sequelize JSON: createdAt (camelCase); DB/RAW: created_at */
      valueGetter: (value, row) => {
        const raw = row?.created_at ?? row?.createdAt;
        if (raw == null || raw === "") return null;
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d.getTime();
      },
      sortComparator: (v1, v2) => (v1 ?? 0) - (v2 ?? 0),
      renderCell: (params) => {
        const raw = params.row?.created_at ?? params.row?.createdAt;
        const d = raw != null && raw !== "" ? new Date(raw) : null;
        const ok = d && !Number.isNaN(d.getTime());
        return (
          <Box sx={{ color: colors.grey[400], fontSize: '0.85rem' }}>
            {ok
              ? d.toLocaleDateString(dateLocale, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/formulas/${params.row.id}`)}
            sx={{
              color: colors.blueAccent[400],
              '&:hover': {
                backgroundColor: `${colors.blueAccent[700]}30`,
              },
            }}
            title={t('view')}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => navigate(`/formulas/edit/${params.row.id}`)}
            sx={{
              color: colors.greenAccent[400],
              '&:hover': {
                backgroundColor: `${colors.greenAccent[700]}30`,
              },
            }}
            title={t('edit')}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row.id)}
            sx={{
              color: colors.redAccent[400],
              '&:hover': {
                backgroundColor: `${colors.redAccent[700]}30`,
              },
            }}
            title={t('delete')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ], [t, colors, navigate, dateLocale]);
  const buildCleanFilters = (source) => {
    const next = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        next[key] = String(value).trim();
      }
    });
    return next;
  };
  const activeFilterCount = Object.keys(appliedFilters).length;
  const handleExcelExport = async () => {
    try {
      setExporting(true);
      const response = await formulaService.exportFormulasExcel(appliedFilters);
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `formulas-${Date.now()}.xlsx`;
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Formula excel export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box m="20px">
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Headeer
            title={t('title')}
            subtitle={t('subtitle')}
          />
          <Box
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: `0 2px 8px ${colors.blueAccent[700]}40`,
              border: `1px solid ${colors.blueAccent[600]}`,
            }}
          >
            <FunctionsIcon sx={{ fontSize: '18px' }} />
            <Box>
              <Box component="span" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                {formulas.length}
              </Box>
              <Box component="span" sx={{ ml: 0.5, fontSize: '0.85rem', opacity: 0.8 }}>
                {formulas.length !== 1 ? t('formulas') : t('formula')}
              </Box>
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/formulas/create')}
          sx={{
            backgroundColor: colors.blueAccent[600],
            color: colors.grey[100],
            fontSize: '14px',
            fontWeight: 'bold',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: `0 4px 12px ${colors.blueAccent[600]}40`,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: colors.blueAccent[700],
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 16px ${colors.blueAccent[600]}60`,
            },
          }}
        >
          {t('createNewFormula')}
        </Button>
      </Box>

      <CollapsibleListFilters
        colors={colors}
        open={filtersOpen}
        onToggle={() => setFiltersOpen((prev) => !prev)}
        activeFilterCount={activeFilterCount}
        onApply={() => {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters(buildCleanFilters(filters));
        }}
        onClear={() => {
          setFilters({ name: "", description: "", dateFrom: "", dateTo: "" });
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters({});
        }}
        exportAction={
          <ListFiltersExcelButton
            colors={colors}
            onClick={handleExcelExport}
            disabled={exporting}
            loading={exporting}
          />
        }
        trailingControls={
          <>
            <TextField
              select
              size="small"
              label="Sort By"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              sx={{ minWidth: 130 }}
              SelectProps={listFiltersSelectProps(colors)}
            >
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="name">Name</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Direction"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              sx={{ minWidth: 120 }}
              SelectProps={listFiltersSelectProps(colors)}
            >
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </>
        }
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={filters.name}
              onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={filters.description}
              onChange={(e) => setFilters((prev) => ({ ...prev, description: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date From"
              value={filters.dateFrom}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date To"
              value={filters.dateTo}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </CollapsibleListFilters>

      {/* DataGrid */}
      <Box
        height="calc(100vh - 250px)"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
            borderRadius: '12px',
            backgroundColor: colors.primary[400],
            boxShadow: `0 4px 24px rgba(0, 0, 0, 0.15)`,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${colors.primary[500]}`,
            color: colors.grey[100],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
            borderRadius: '12px 12px 0 0',
            color: colors.grey[100],
            fontSize: '14px',
            fontWeight: 600,
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
            borderRadius: '0 0 12px 12px',
            color: colors.grey[100],
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: `${colors.blueAccent[800]}20`,
            },
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={formulas}
          columns={columns}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('deleteFormulaTitle')}
        description={t('deleteFormulaDescription')}
        onClose={() => {
          setDeleteDialogOpen(false);
          setFormulaToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor: snackbar.severity === "success" 
              ? colors.greenAccent[700] 
              : colors.redAccent[700],
            color: "white",
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Formulas;

